import React, { useCallback, useEffect, useRef, useState } from "react"

import { useTranslation } from "react-i18next"
import { useMutation, useQueryClient } from "react-query"
import { useParams } from "react-router-dom"

import CheckIcon from "@mui/icons-material/Check"
import ClearIcon from "@mui/icons-material/Clear"
import { Grid } from "@mui/material"
import { NotificationUtils, Snackbar, Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosError } from "axios"
import { useFormikContext } from "formik"

import { Dropzone } from "../../../components/form/Dropzone"
import type { SchemaFeature, WorkflowSimulationFormik } from "../../../features/Workflows/types/interfaces"
import { getTheme } from "../../../hooks/UseTheme"
import { KonanAPI } from "../../../services/KonanAPI"
import { DataFileGroup } from "../../../types/generated/api/DataFileGroup"
import { getEnvVar, getKeysPathsRecursively, readFileContentAsync } from "../../../utils/genericHelpers"
import type { NewDatasetViewProps } from "../Interfaces"

import styles from "./DataSource.module.scss"

/**
 * NewDatasetView Component
 * Handles the upload of new datasets (CSV/JSON) for simulation workflows.
 * Validates uploaded files against required schema features and provides visual feedback.
 *
 * @component
 * @param {NewDatasetViewProps} props
 * @param {WorkflowVersionsRetrieve} props.workflow - The workflow configuration containing schema requirements
 * @param {Function} props.setDataFile - Callback function to set the data file UUID and columns
 * @return {React.ReactElement}
 */
export function NewDatasetView(props: Readonly<NewDatasetViewProps>): React.ReactElement {
  const { workflow, setDataFile } = props

  const { id: projectID } = useParams<{ id: string }>()

  const { setFieldValue, validateForm } = useFormikContext<WorkflowSimulationFormik>()

  const { t } = useTranslation("simulations", {
    keyPrefix: "simulationCreationDialog.newDatasetView",
  })

  const abortControllerRef = useRef<AbortController | null>(null)

  const queryClient = useQueryClient()
  const theme = getTheme()

  const maxSize = Number(getEnvVar("DATA_FILE_MAX_SIZE", 2048)) * 1000000 // converting MB -> Bytes

  const [uploadProgress, setUploadProgress] = React.useState<number>(0)
  const [acceptedFiles, setAcceptedFiles] = useState<File[]>([])
  const [uploadedColumns, setUploadedColumns] = useState<Array<string>>([])

  /**
   * Handles file upload using react-query mutation
   * Processes the file and updates the columns state based on file type (CSV/JSON)
   * @param {File} file - The file to be uploaded
   * @returns {Promise<DataFileGroup>} The uploaded file data
   */
  const {
    isLoading: uploadingCSV,
    isError,
    mutateAsync: uploadCSVAsyncMutation,
    reset,
  } = useMutation<DataFileGroup, AxiosError, File>(
    (file: File) => {
      abortControllerRef.current = new AbortController()
      return KonanAPI.uploadDataFile({
        project_uuid: projectID as string,
        file: file,
        setProgress: setUploadProgress,
        signal: abortControllerRef.current.signal,
        type: DataFileGroup.type.PREDICTIONS,
      })
    },
    {
      mutationKey: "uploadCSV",
      onSuccess: (res) => {
        if (!acceptedFiles[0]?.name.endsWith(".json")) setUploadedColumns([...(res.active_version.columns ?? [])])
        setDataFile(res.uuid, [...(res.active_version.columns ?? [])])

        // disabling the next btn if required feats aren't met
        setShouldProceedFlag(res.active_version.columns ?? [])

        NotificationUtils.toast(t("notifications.uploadCSV.success"), {
          severity: "success",
        })
      },
    },
  )

  /**
   * Validates if all required features are present in the uploaded columns
   * Updates the form state accordingly
   * @param {Array<string>} columns - Array of column names from the uploaded file
   */
  const setShouldProceedFlag = (columns: Array<string>): void => {
    const shouldProceed = !(workflow?.schema?.features as SchemaFeature[])
      ?.filter((feat) => feat?.is_required)
      .some((feat) => !columns?.find((col) => col === feat?.name))

    // setting every time to invalidate cached data in formik
    setFieldValue("shouldProceed", shouldProceed)

    setTimeout(() => {
      validateForm()
    }, 100)
  }

  /**
   * Aborts ongoing upload and resets component state
   */
  const handleCloseAndAbort = useCallback(() => {
    abortControllerRef.current?.abort()
    reset()
    setUploadProgress(0)
    setAcceptedFiles([])
  }, [reset])

  /**
   * Handles the file upload process
   * For JSON files, extracts column names from the first 5 records
   * For CSV files, uses the columns provided by the API
   */
  const handleUpload = async (): Promise<void> => {
    try {
      if (!acceptedFiles[0]) return

      await uploadCSVAsyncMutation(acceptedFiles[0])
      // Invalidate training data to trigger a refresh
      await queryClient.invalidateQueries(["data-files", projectID])

      if (acceptedFiles[0].name.endsWith(".json")) {
        const columns = new Set<string>()
        const fileContent = JSON.parse(await readFileContentAsync(acceptedFiles[0])).slice(0, 5)

        for (const entry of fileContent) {
          getKeysPathsRecursively(entry).forEach((key: string) => columns.add(key))
        }

        setUploadedColumns(Array.from(columns))

        setShouldProceedFlag(Array.from(columns))
      }
    } catch {
      // reset dropzone on error
      handleCloseAndAbort()
    }
  }

  // starts uploading once the file is read
  useEffect(() => {
    if (acceptedFiles && acceptedFiles.length > 0) {
      handleUpload()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acceptedFiles])

  return (
    <Grid container spacing={2}>
      {/** request didn't succeed error */}
      {isError && (
        <Grid item xs={12}>
          <Snackbar severity="error" fullWidth description={t("snackbar.uploadCSVError.description")} />
        </Grid>
      )}

      {/* DropZone */}
      <Grid item xs={12}>
        <Dropzone
          uploadingCSV={uploadingCSV}
          uploadProgress={uploadProgress}
          resetProgress={() => setUploadProgress(0)}
          selectedFiles={acceptedFiles}
          setSelectedFiles={(file: File[]) => {
            file.length === 0 && setFieldValue("shouldProceed", false)
            setAcceptedFiles(file)
          }}
          bottomText={t("dropzone.bottomText")}
          maxSize={maxSize}
          extensions={{ "application/json": [".json"], "text/*": [".csv"] }}
          titleHeader={{
            title: t("title"),
            tooltip: t("titleTooltip"),
          }}
          description={t("dropzone.title")}
          handleCancelUpload={handleCloseAndAbort}
        />
      </Grid>

      {/* Features table, enabled only if schema exists */}
      {(workflow?.schema?.features as SchemaFeature[])?.filter((feat) => feat?.is_required)?.length > 0 && (
        <Grid container item mt={1} ml={2} className={styles.existingDataSetTable}>
          <Grid item xs={12}>
            <Typography variant="a" className={styles.requiredFeaturesHeader}>
              {t("requiredFeaturesHeader")}
            </Typography>
          </Grid>

          {(workflow?.schema?.features as SchemaFeature[])
            ?.filter((feat) => feat?.is_required)
            .sort((a, b) => Number(a) - Number(b))
            .map((item) => (
              <Grid container item xs={12} className={styles.existingDataSetRow} key={item?.name}>
                <Grid item xs={acceptedFiles?.length > 0 ? 8 : 12}>
                  <Typography variant="a" noWrap>
                    {item?.name}
                  </Typography>
                </Grid>

                {acceptedFiles?.length > 0 && uploadedColumns?.length > 0 && (
                  <Grid item xs={4} container>
                    <Grid item mr={1} pt={0.5}>
                      {uploadedColumns?.find((feat) => feat === item?.name) ? (
                        <CheckIcon fontSize="small" style={{ color: theme.palette.green.text[2] }} />
                      ) : (
                        <ClearIcon fontSize="small" style={{ color: theme.palette.red.text[2] }} />
                      )}
                    </Grid>
                    <Grid item alignSelf={"center"}>
                      <Typography variant="p" variantColor={2} noWrap>
                        {uploadedColumns?.find((feat) => feat === item?.name)
                          ? t("featureStatus.found")
                          : t("featureStatus.notFound")}
                      </Typography>
                    </Grid>
                  </Grid>
                )}
              </Grid>
            ))}
        </Grid>
      )}
    </Grid>
  )
}
